import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Save, Plug, AlertTriangle, ShieldCheck, Loader2 } from "lucide-react";

/**
 * /admin/paddle-readiness — Payment readiness dashboard.
 * Tracks the 14 pre-flight items required before enabling Paddle
 * Production. Also lets the admin fill in Paddle price IDs and see
 * recent webhook events.
 */
export default function PaddleReadiness() {
    const [readiness, setReadiness] = useState(null);
    const [plans, setPlans] = useState([]);
    const [events, setEvents] = useState([]);

    const load = async () => {
        const [r, p, e] = await Promise.all([
            api.get("/admin/paddle/readiness"), api.get("/admin/paddle/plans"),
            api.get("/admin/paddle/events?limit=25"),
        ]);
        setReadiness(r.data); setPlans(p.data.plans || []); setEvents(e.data.events || []);
    };
    useEffect(() => { load(); }, []);

    const patchReadiness = async (item, status) => {
        await api.patch("/admin/paddle/readiness", { item, status });
        toast.success(`${item} → ${status}`);
        load();
    };

    if (!readiness) return <div className="p-8"><Loader2 size={16} className="animate-spin" /></div>;

    const { items, allowed_statuses, blockers, payment_ready, environment, mock_mode } = readiness;

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="paddle-readiness">
            <div className="mb-4 flex items-center gap-2 text-xs">
                <Link to="/compliance-ops" className="text-stone-500 hover:text-stone-900 flex items-center gap-1"><ArrowLeft size={12} /> Compliance Ops</Link>
            </div>
            <div className="flex items-start gap-3 mb-6">
                <div className="mt-1 rounded-full bg-blue-50 p-1.5"><Plug size={16} className="text-blue-600" /></div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Paddle Payment Readiness</h1>
                    <p className="text-sm text-stone-500 mt-1">Environment: <b>{environment}</b>{mock_mode && " · MOCK MODE (webhook signature verification bypassed with X-Paddle-Mock: 1)"}</p>
                    <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${payment_ready ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="readiness-summary">
                        {payment_ready ? <ShieldCheck size={12} /> : <AlertTriangle size={12} />}
                        {payment_ready ? "Payment Ready" : `${blockers} blocker(s) · Do NOT enable production`}
                    </div>
                </div>
            </div>

            {/* Readiness checklist */}
            <section className="rounded-xl border border-stone-200 bg-white divide-y divide-stone-100" data-testid="readiness-checklist">
                {items.map((it) => (
                    <div key={it.item} className="flex items-center justify-between gap-4 p-3">
                        <div className="text-sm font-medium">{it.item}</div>
                        <select
                            value={it.status}
                            onChange={(e) => patchReadiness(it.item, e.target.value)}
                            data-testid={`readiness-${it.item.replace(/\s+/g, "-").toLowerCase()}`}
                            className="rounded-md border border-stone-300 bg-white px-2 py-1 text-xs"
                        >
                            {allowed_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                ))}
            </section>

            {/* Plan mapping */}
            <h2 className="mt-8 text-lg font-bold">Plan → Paddle mapping</h2>
            <p className="text-xs text-stone-500 mt-1 max-w-2xl">Paste the sandbox Paddle price IDs here after creating your products in Paddle Catalog. These IDs feed the public pricing page.</p>
            <div className="mt-4 space-y-3">
                {["essential", "pro", "family", "business"].map((slug) => {
                    const p = plans.find((x) => x.internal_plan_slug === slug) || { internal_plan_slug: slug };
                    return <PlanRow key={slug} plan={p} onSave={load} />;
                })}
            </div>

            {/* Recent events */}
            <h2 className="mt-8 text-lg font-bold">Recent Paddle events</h2>
            <div className="mt-3 rounded-xl border border-stone-200 bg-white divide-y divide-stone-100 max-h-96 overflow-auto" data-testid="paddle-events">
                {events.length === 0 && <div className="p-4 text-sm text-stone-400 text-center">No webhook events received yet.</div>}
                {events.map((e) => (
                    <div key={e.event_id} className="p-3 flex items-center justify-between text-xs">
                        <div>
                            <div className="font-mono">{e.event_type}</div>
                            <div className="text-stone-500">{e.event_id?.slice(0, 20)}… · {e.received_at?.slice(0, 19).replace("T", " ")}</div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${e.processing_status === "processed" ? "bg-emerald-50 text-emerald-700" : e.processing_status === "failed" ? "bg-rose-50 text-rose-700" : "bg-stone-100 text-stone-600"}`}>
                            {e.processing_status}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PlanRow({ plan, onSave }) {
    const [f, setF] = useState({
        display_name: plan.display_name || plan.internal_plan_slug,
        paddle_product_id: plan.paddle_product_id || "",
        paddle_monthly_price_id: plan.paddle_monthly_price_id || "",
        paddle_annual_price_id: plan.paddle_annual_price_id || "",
        currency: plan.currency || "USD",
        trial_days: plan.trial_days || 0,
        active_status: plan.active_status !== false,
        display_order: plan.display_order || 0,
    });
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/admin/paddle/plans/${plan.internal_plan_slug}`, f);
            toast.success(`${plan.internal_plan_slug} mapping saved`);
            onSave();
        } catch { toast.error("Save failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid={`plan-${plan.internal_plan_slug}`}>
            <div className="flex items-center justify-between gap-3 mb-3">
                <div className="text-sm font-semibold capitalize">{plan.internal_plan_slug}</div>
                <label className="inline-flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={!!f.active_status} onChange={(e) => setF({ ...f, active_status: e.target.checked })} /> Active
                </label>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
                <Input placeholder="Display name" value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} data-testid={`plan-${plan.internal_plan_slug}-name`} />
                <Input placeholder="Paddle product ID (pro_...)" value={f.paddle_product_id} onChange={(e) => setF({ ...f, paddle_product_id: e.target.value })} data-testid={`plan-${plan.internal_plan_slug}-product`} />
                <Input placeholder="Monthly price ID (pri_...)" value={f.paddle_monthly_price_id} onChange={(e) => setF({ ...f, paddle_monthly_price_id: e.target.value })} data-testid={`plan-${plan.internal_plan_slug}-monthly`} />
                <Input placeholder="Annual price ID (pri_...)" value={f.paddle_annual_price_id} onChange={(e) => setF({ ...f, paddle_annual_price_id: e.target.value })} data-testid={`plan-${plan.internal_plan_slug}-annual`} />
                <Input placeholder="Currency" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} />
                <Input type="number" placeholder="Trial days" value={f.trial_days} onChange={(e) => setF({ ...f, trial_days: Number(e.target.value) || 0 })} />
            </div>
            <Button className="mt-3" size="sm" onClick={save} disabled={busy} data-testid={`plan-${plan.internal_plan_slug}-save`}>
                <Save size={12} className="mr-1" /> {busy ? "Saving…" : "Save mapping"}
            </Button>
        </div>
    );
}
